/**
 * Local Business Page - Google Business Profile Dashboard
 * 
 * Migrated from seo-engine-webtools-customer-module
 */

import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Skeleton,
  Rating,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Fade,
  Button,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useLocalBusiness } from '@/hooks/customer';
import { formatCompactNumber, formatRelativeTime, formatPercentage } from '@/services/customer/formatters';
import { MetricCard, InlineError, MetricCardSkeleton } from '@/components/customer';

function CustomerLocalBusinessPage() {
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useLocalBusiness();

  if (isLoading) {
    return (
      <Box>
        <Box sx={{ mb: 3 }}>
          <Skeleton variant="text" width={200} height={40} />
          <Skeleton variant="text" width={350} height={24} />
        </Box>
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={6} md={3} key={i}>
              <MetricCardSkeleton />
            </Grid>
          ))}
        </Grid>
        <Grid container spacing={3}>
          {[1, 2].map((i) => (
            <Grid item xs={12} md={6} key={i}>
              <Card>
                <CardContent>
                  <Skeleton variant="text" width="40%" height={28} sx={{ mb: 2 }} />
                  {[1, 2, 3].map((j) => (
                    <Box key={j} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                        <Skeleton variant="circular" width={40} height={40} />
                        <Box sx={{ flex: 1 }}>
                          <Skeleton variant="text" width="60%" height={20} />
                          <Skeleton variant="text" width="30%" height={16} />
                        </Box>
                      </Box>
                      <Skeleton variant="text" width="100%" height={16} />
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Fade in timeout={300}>
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/customer')}
          sx={{ mb: 2 }}
        >
          Back to Dashboard
        </Button>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            Local Business
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Google Business Profile performance and reviews
          </Typography>
        </Box>

        {error && (
          <InlineError error={error} title="Failed to load local business data" onRetry={refetch} />
        )}

        {/* Overview Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Average Rating
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="h3" sx={{ mr: 1 }}>
                    {data?.overview.averageRating.toFixed(1)}
                  </Typography>
                  <Rating value={data?.overview.averageRating ?? 0} readOnly precision={0.1} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} md={3}>
            <MetricCard
              title="Total Reviews"
              value={formatCompactNumber(data?.overview.totalReviews ?? 0)}
              tooltip="Total number of customer reviews received."
            />
          </Grid>

          <Grid item xs={6} md={3}>
            <MetricCard
              title="Response Rate"
              value={formatPercentage(data?.overview.responseRate ?? 0)}
              tooltip="Percentage of reviews that received a response."
            />
          </Grid>

          <Grid item xs={6} md={3}>
            <MetricCard
              title="Avg Response Time"
              value={`${data?.overview.avgResponseTimeHours.toFixed(1)}h`}
              tooltip="Average time to respond to reviews."
            />
          </Grid>
        </Grid>

        {/* Performance Metrics */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Performance Metrics ({data?.metrics.period})
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} md={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Search Views
                </Typography>
                <Typography variant="h5">
                  {formatCompactNumber(data?.metrics.searchViews ?? 0)}
                </Typography>
              </Grid>
              <Grid item xs={6} md={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Map Views
                </Typography>
                <Typography variant="h5">
                  {formatCompactNumber(data?.metrics.mapViews ?? 0)}
                </Typography>
              </Grid>
              <Grid item xs={6} md={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Website Clicks
                </Typography>
                <Typography variant="h5">
                  {formatCompactNumber(data?.metrics.websiteClicks ?? 0)}
                </Typography>
              </Grid>
              <Grid item xs={6} md={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Direction Requests
                </Typography>
                <Typography variant="h5">
                  {formatCompactNumber(data?.metrics.directionRequests ?? 0)}
                </Typography>
              </Grid>
              <Grid item xs={6} md={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Phone Calls
                </Typography>
                <Typography variant="h5">
                  {formatCompactNumber(data?.metrics.phoneCallClicks ?? 0)}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Reviews and Q&A */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Recent Reviews
                </Typography>
                <List>
                  {data?.recentReviews.map((review, index) => (
                    <Box key={review.id}>
                      {index > 0 && <Divider />}
                      <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                        <ListItemAvatar>
                          <Avatar>{review.reviewerName.charAt(0)}</Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="subtitle2">{review.reviewerName}</Typography>
                              <Rating value={review.rating} size="small" readOnly />
                            </Box>
                          }
                          secondary={
                            <>
                              <Typography variant="body2" color="text.primary" sx={{ mt: 0.5 }}>
                                {review.comment || 'No comment'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatRelativeTime(review.createTime)}
                              </Typography>
                              {review.reviewReply && (
                                <Box sx={{ mt: 1, pl: 2, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                                  <Typography variant="caption" color="primary" fontWeight="medium">
                                    Business Response:
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {review.reviewReply}
                                  </Typography>
                                </Box>
                              )}
                            </>
                          }
                        />
                      </ListItem>
                    </Box>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Questions & Answers
                </Typography>
                <List>
                  {data?.pendingQAs.map((qa, index) => (
                    <Box key={qa.id}>
                      {index > 0 && <Divider />}
                      <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                        <ListItemText
                          primary={
                            <Typography variant="subtitle2">{qa.question}</Typography>
                          }
                          secondary={
                            <>
                              <Typography variant="caption" color="text.secondary">
                                Asked by {qa.questionerName} • {formatRelativeTime(qa.questionTime)}
                              </Typography>
                              {qa.answer && (
                                <Box sx={{ mt: 1, pl: 2, borderLeft: '3px solid', borderColor: 'success.main' }}>
                                  <Typography variant="caption" color="success.main" fontWeight="medium">
                                    Answer from {qa.answererName}:
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {qa.answer}
                                  </Typography>
                                </Box>
                              )}
                            </>
                          }
                        />
                      </ListItem>
                    </Box>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Fade>
  );
}

export default CustomerLocalBusinessPage;
